import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useNotification from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { getErrorMessage } from '@/utils/errorUtils';
import { MAX_SHIPPINGS_ON_MAP } from '@/pages/Business/pages/Shippings/config';
import { useLazyGetRouteCloneCandidatesQuery } from '../services';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import type { RouteCloneCandidates } from '../models';

const CLONE_ERROR_FALLBACK = 'Error al clonar la ruta cancelada';

/**
 * Un 404 acá no es "la ruta no existe": el detalle de la ruta se está viendo en
 * pantalla. Es que el backend contra el que pega esta build todavía no expone
 * `/clone-candidates`. Sin este caso el usuario ve el texto genérico y no tiene
 * forma de distinguirlo de un fallo real.
 */
const resolveCloneErrorMessage = (error: unknown): string => {
  const status = (error as { status?: unknown })?.status;
  if (status === 404) {
    return 'La función de clonar rutas no está disponible en esta versión del servidor. Avisá a TI para que actualicen el backend.';
  }
  return getErrorMessage(error, CLONE_ERROR_FALLBACK);
};

export interface UseCloneCancelledRouteParams {
  routeId: string;
  logisticOperatorId?: string | null;
}

/**
 * Clona una ruta cancelada: pide al back los pedidos que hoy siguen siendo
 * ruteables y abre la pantalla de ruteo con ellos preseleccionados. No crea ni
 * genera ninguna ruta — equivale a seleccionar esos pedidos en el listado y
 * tocar "Rutear".
 */
export function useCloneCancelledRoute({
  routeId,
  logisticOperatorId,
}: UseCloneCancelledRouteParams) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { routeBuilderBasePath } = useMonitoringIntegration();
  const [fetchCandidates] = useLazyGetRouteCloneCandidatesQuery();
  const [isCloning, setIsCloning] = useState(false);

  const notifyExclusions = useCallback(
    (candidates: RouteCloneCandidates) => {
      if (candidates.summary.excluded === 0) return;
      const excludedNumbers = candidates.excluded
        .map((item) => item.shippingNumber)
        .join(', ');
      showNotification(
        `Se excluyeron ${candidates.summary.excluded} pedido(s) que ya no se pueden rutear: ${excludedNumbers}`,
        NotificationSeverity.warning
      );
    },
    [showNotification]
  );

  const cloneRoute = useCallback(async (): Promise<boolean> => {
    const operatorId = logisticOperatorId?.trim() ?? '';
    if (!operatorId) {
      showNotification(
        'El operador logístico es requerido para clonar la ruta.',
        NotificationSeverity.error
      );
      return false;
    }

    setIsCloning(true);
    try {
      const candidates = await fetchCandidates({
        routeId,
        logisticOperatorId: operatorId,
      }).unwrap();

      if (candidates.shippingNumbers.length === 0) {
        showNotification(
          candidates.summary.total === 0
            ? 'La ruta cancelada no tiene pedidos para clonar.'
            : 'Ninguno de los pedidos de la ruta cancelada se puede rutear en este momento.',
          NotificationSeverity.warning
        );
        return false;
      }

      if (candidates.shippingNumbers.length > MAX_SHIPPINGS_ON_MAP) {
        showNotification(
          `No se pueden cargar más de ${MAX_SHIPPINGS_ON_MAP} pedidos en el mapa. La ruta cancelada tiene ${candidates.shippingNumbers.length} pedidos ruteables.`,
          NotificationSeverity.error
        );
        return false;
      }

      notifyExclusions(candidates);

      const params = new URLSearchParams({
        numbers: candidates.shippingNumbers.join(','),
        logisticOperatorId: candidates.logisticOperatorId || operatorId,
      });
      navigate(`${routeBuilderBasePath}?${params.toString()}`);
      return true;
    } catch (error: unknown) {
      showNotification(
        getScopeErrorMessage(error) ?? resolveCloneErrorMessage(error),
        NotificationSeverity.error
      );
      return false;
    } finally {
      setIsCloning(false);
    }
  }, [
    routeId,
    logisticOperatorId,
    fetchCandidates,
    showNotification,
    notifyExclusions,
    navigate,
    routeBuilderBasePath,
  ]);

  return { cloneRoute, isCloning };
}

export default useCloneCancelledRoute;
